import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router'
import { BaseConfig } from '@/configs/base.config'
import { routes } from './routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.name === 'Users' && String(from.name).startsWith('User-')) {
      return false
    }
    if (savedPosition) {
      return savedPosition
    }
    return {
      top: 0,
      left: 0,
      behavior: 'smooth',
    }
  },
})

export type MiddlewareFn = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => void | Promise<void>

// Import all middlewares automatically (typed)
const middlewareContext = import.meta.glob<{ default: MiddlewareFn }>('../middleware/*.ts', {
  eager: true,
})

function getMiddleware(name: string): MiddlewareFn | null {
  const mod = middlewareContext[`../middleware/${name}.ts`]
  return mod?.default ?? null
}

// Middleware runner
router.beforeEach(async (to, from, next) => {
  const title = typeof to.meta.title === 'function' ? to.meta.title(to) : to.meta.title
  document.title = title ? title + BaseConfig.DEFAULT_SUFFIX : BaseConfig.DEFAULT_TITLE
  const middlewares = to.meta.middleware as string[] | undefined
  if (!middlewares || middlewares.length === 0) {
    return next()
  }

  // const context = { to, from, next }

  const runMiddleware = async (index: number) => {
    const name = middlewares[index]
    if (!name) return next()

    const middleware = getMiddleware(name)
    if (!middleware) {
      console.warn(`⚠️ Middleware "${name}" not found`)
      return next()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await middleware(to, from, (param?: any) => {
      if (param === false || typeof param === 'object') {
        return next(param)
      }
      runMiddleware(index + 1)
    })
  }

  await runMiddleware(0)
})

export default router
